import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Video, ExternalLink, Ticket, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { EventAdminActions } from '@/components/events/EventAdminActions';

export interface Event {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  slug: string | null;
  banner: string | null;
  locationType: 'PHYSICAL' | 'VIRTUAL' | 'TO_BE_DECIDED';
  address: string | null;
  virtualUrl: string | null;
  isRegistered: boolean;
  isAdmin: boolean;
  registrationsCount?: number;
  attendanceSelfMark?: {
    activityId: string;
    buttonLabel: string;
    status: 'PENDING' | 'CONFIRMED' | null;
  } | null;
}

function stripHtml(html: string) {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function EventCard({ event, communityId }: { event: Event; communityId: string }) {
  const navigate = useNavigate();
  const startDate = new Date(event.startDate);
  const isPast = new Date(event.endDate) < new Date();
  const eventPath = `/${communityId}/events/${event.slug || event.id}`;
  const needsSelfMark = Boolean(event.attendanceSelfMark && !event.attendanceSelfMark.status);
  const showFooter = !isPast || event.isRegistered;

  const openEvent = () => navigate(eventPath);

  return (
    <Card
      role="link"
      tabIndex={0}
      onClick={openEvent}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openEvent();
        }
      }}
      className={`overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow relative group cursor-pointer ${
        isPast ? 'opacity-80 grayscale-[0.3]' : ''
      }`}
    >
      {event.isAdmin ? (
        <EventAdminActions communityId={communityId} eventId={event.id} variant="card" />
      ) : null}

      <div className="aspect-video w-full overflow-hidden relative bg-muted flex items-center justify-center">
        {event.banner ? (
          <img
            src={event.banner}
            alt={event.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-muted-foreground/40">
            <Calendar className="h-12 w-12 mb-2" />
            <span className="text-xs font-medium uppercase tracking-wider">Event</span>
          </div>
        )}

        {isPast && (
          <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
            <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm">
              Past Event
            </Badge>
          </div>
        )}
      </div>

      <CardHeader className="p-5 pb-2 pt-2 space-y-0">
        <div className="flex justify-between items-start gap-2 mb-1.5 flex-wrap">
          <Badge variant={event.isRegistered ? 'secondary' : 'outline'}>
            {event.isRegistered ? 'Going' : event.locationType}
          </Badge>
          {event.attendanceSelfMark?.status === 'CONFIRMED' ? (
            <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 hover:bg-green-500/15">
              Checked in
            </Badge>
          ) : event.attendanceSelfMark?.status === 'PENDING' ? (
            <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15">
              Awaiting confirm
            </Badge>
          ) : null}
        </div>

        <CardTitle className="line-clamp-2 text-lg leading-snug tracking-tight !mt-0">
          {event.title}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 p-5 pt-0 space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 shrink-0 text-foreground/70" />
          <span className="line-clamp-1">
            {startDate.toLocaleDateString(undefined, {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
            {' • '}
            {startDate.toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>

        <div className="flex items-center gap-2 !m-0">
          {event.locationType === 'PHYSICAL' ? (
            <MapPin className="h-4 w-4 shrink-0 text-foreground/70" />
          ) : event.locationType === 'VIRTUAL' ? (
            <Video className="h-4 w-4 shrink-0 text-foreground/70" />
          ) : (
            <ExternalLink className="h-4 w-4 shrink-0 text-foreground/70" />
          )}

          <span className="line-clamp-1">
            {event.locationType === 'PHYSICAL'
              ? event.address
              : event.locationType === 'VIRTUAL'
                ? 'Online Event'
                : 'To be decided'}
          </span>
        </div>

        {typeof event.registrationsCount === 'number' && event.registrationsCount > 0 && (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 shrink-0 text-foreground/70" />
            <span className="line-clamp-1 font-medium text-foreground/80">
              {event.registrationsCount} registration{event.registrationsCount === 1 ? '' : 's'}
            </span>
          </div>
        )}

        {event.description && (
          <div className="pt-1.5 mt-1">
            <p
              className="text-sm text-foreground/80 line-clamp-2"
              title={stripHtml(event.description)}
            >
              {stripHtml(event.description)}
            </p>
          </div>
        )}
      </CardContent>

      {showFooter ? (
        <CardFooter className="p-4 px-5 mt-auto border-t bg-muted/5 flex gap-2">
          {event.isRegistered ? (
            needsSelfMark ? (
              <>
                <Button
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/${communityId}/events/${event.id}/activities/${event.attendanceSelfMark!.activityId}/self-mark`
                    );
                  }}
                >
                  {event.attendanceSelfMark!.buttonLabel || "I'm here"}
                </Button>
                <Button
                  className="shrink-0 w-10 flex-none"
                  variant="outline"
                  size="icon"
                  title="View Ticket"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${eventPath}?ticket=true`);
                  }}
                >
                  <Ticket className="h-5 w-5" />
                </Button>
              </>
            ) : (
              <Button
                className="flex-1"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${eventPath}?ticket=true`);
                }}
              >
                <Ticket className="h-4 w-4 mr-2" />
                View Ticket
              </Button>
            )
          ) : (
            <Button
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${eventPath}?going=1`);
              }}
            >
              I&apos;m Going
            </Button>
          )}
        </CardFooter>
      ) : null}
    </Card>
  );
}
